'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'data-fetching-client';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import SanitizedText from 'sanitize-text/components/SanitizedText';
import styled from 'styled-components';
import H4 from 'UIComponents/elements/headings/H4';
import Small from 'UIComponents/elements/Small';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIBadge from 'UIComponents/badge/UIBadge';
import UIPaginator from 'UIComponents/paginator/UIPaginator';
import UIDropdownDivider from 'UIComponents/dropdown/UIDropdownDivider';
import * as RequestStates from '../../constants/RequestState';
import { GET_VERSION_HISTORY } from '../../queries/versionHistoryQuery';
import VersionHistoryResponse from '../../records/VersionHistoryResponse';
import LoadingWrapper from '../LoadingWrapper';
const HeaderRow = styled(UIFlex).withConfig({
  displayName: "VersionHistory__HeaderRow",
  componentId: "sc-4vke8c-0"
})(["&&{width:100%;}"]);
const ActionsWrapper = styled.div.withConfig({
  displayName: "VersionHistory__ActionsWrapper",
  componentId: "sc-4vke8c-1"
})(["margin-left:auto;"]);
const EMPTY_VERSION_HISTORY = new VersionHistoryResponse();
const MAX_VERSIONS_PER_PAGE = 20;

const Version = ({
  renderVersionActions,
  showDivider = true,
  showInstalled = false,
  version
}) => /*#__PURE__*/_jsxs(UIFlex, {
  direction: "column",
  className: "m-bottom-4",
  children: [/*#__PURE__*/_jsxs(HeaderRow, {
    className: "m-bottom-1",
    children: [/*#__PURE__*/_jsx(H4, {
      "aria-level": 5,
      className: "m-bottom-0",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "ecosystemMarketplaceCore.version.number",
        options: {
          versionNumber: version.versionNumber
        }
      })
    }), showInstalled && /*#__PURE__*/_jsx(UIBadge, {
      className: "m-left-2 m-top-1",
      use: "koala",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "ecosystemMarketplaceCore.version.installed"
      })
    }), renderVersionActions && /*#__PURE__*/_jsx(ActionsWrapper, {
      children: renderVersionActions(version)
    })]
  }), /*#__PURE__*/_jsx(Small, {
    use: "help",
    className: "m-bottom-2",
    children: /*#__PURE__*/_jsx(FormattedDateTime, {
      value: version.publishedAt,
      format: "ll"
    })
  }), /*#__PURE__*/_jsx(SanitizedText, {
    body: version.releaseNotes
  }), showDivider && /*#__PURE__*/_jsx(UIDropdownDivider, {})]
});

const VersionHistory = ({
  errorMessage,
  installedVersionNumber,
  numVersions,
  offeringId,
  onTrackInteraction,
  renderVersionActions,
  versionHistoryQuery
}) => {
  const [page, setPage] = useState(1);
  const {
    offset,
    limit
  } = useMemo(() => ({
    offset: (page - 1) * MAX_VERSIONS_PER_PAGE,
    limit: numVersions && numVersions <= MAX_VERSIONS_PER_PAGE * page ? numVersions : MAX_VERSIONS_PER_PAGE
  }), [numVersions, page]);
  const {
    loading,
    error,
    data,
    previousData
  } = useQuery(versionHistoryQuery, {
    variables: {
      offeringId,
      offset,
      limit
    }
  });
  const versionHistory = useMemo(() => {
    if (!data && !previousData) {
      return EMPTY_VERSION_HISTORY;
    }

    const {
      versionHistory: versionHistoryData
    } = data || previousData;
    return new VersionHistoryResponse(versionHistoryData);
  }, [data, previousData]);
  const versionHistoryRequestState = useMemo(() => {
    if (loading) {
      return RequestStates.PENDING;
    }

    if (error) {
      return RequestStates.FAILED;
    }

    if (!data && !previousData) {
      return RequestStates.UNINITIALIZED;
    }

    return RequestStates.SUCCEEDED;
  }, [data, error, loading, previousData]);
  const pageCount = Math.ceil((numVersions || versionHistory.total) / MAX_VERSIONS_PER_PAGE);
  const needsPagination = pageCount > 1;
  const handlePageChange = useCallback(newPage => {
    setPage(newPage);

    if (onTrackInteraction) {
      onTrackInteraction('change version history page', {
        page: newPage
      });
    }
  }, [onTrackInteraction]);
  return /*#__PURE__*/_jsxs(LoadingWrapper, {
    errorMessage: errorMessage,
    requestStatus: versionHistoryRequestState,
    showLoadingOnUninitialized: true,
    children: [versionHistory.versionHistory.map(version => /*#__PURE__*/_jsx(Version, {
      version: version,
      showDivider: version !== versionHistory.versionHistory.get(versionHistory.versionHistory.size - 1),
      showInstalled: version.versionNumber === installedVersionNumber,
      renderVersionActions: renderVersionActions
    }, version.versionNumber)), needsPagination && /*#__PURE__*/_jsx(UIPaginator, {
      onPageChange: ({
        target: {
          value
        }
      }) => handlePageChange(value),
      page: page,
      pageCount: pageCount,
      maxVisiblePageButtons: 5
    })]
  });
};

VersionHistory.propTypes = {
  errorMessage: PropTypes.string,
  installedVersionNumber: PropTypes.number,
  numVersions: PropTypes.number,
  offeringId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onTrackInteraction: PropTypes.func,
  renderVersionActions: PropTypes.func,
  versionHistoryQuery: PropTypes.object
};
VersionHistory.defaultProps = {
  versionHistoryQuery: GET_VERSION_HISTORY
};
export default VersionHistory;